import { Controller } from "@hotwired/stimulus";

/**
 * Pricing Controller - Price display & recurrence management
 *
 * Responsibilities:
 * • Monthly/yearly recurrence switching & display
 * • Viewer dropdown pricing updates (premium plans)
 * • Price display from backend data
 * • Currency change handling via currency:changed events
 * • Inter-controller communication via plan-changed events
 * • Inline addon section price updates
 */

export default class extends Controller {
  static REC_MONTH = "month";
  static REC_YEAR = "year";
  static INLINE_ADDON_SELECTOR = "#inline-addon-section";

  static DEFAULTS = {
    DEFAULT_RECURRENCE: "year"
  };

  static targets = [
    "monthlyPrice",
    "yearlyPrice",
    "planSchedule",
    "stepNumber",
    "monthlyAmount",
    "yearlyAmount",
    "savingsAmount",
    "billingText",
    "billingAmount",
    "viewerSelect",
    "planCard",
    "annualComparison",
    "annualTotal"
  ];

  static values = {
    selectedPlan: String,
    recurrence: String,
    currentRecurrence: String,
    currentPlan: String,
    nonRecurringAddons: Array,
    initialAddonsByCategory: Object,
    prefillFeatures: Object
  };

  connect() {
    this._requestId = 0;
    this.recurrenceValue ||= this.currentRecurrenceValue || this.constructor.DEFAULTS.DEFAULT_RECURRENCE;

    this.toggleRecurrenceDisplay();
    this.initializeViewerCount();
    this.bindEvents();
    this.updateAnnualBillingNotes();
    this.addonPricesByCategory = {};
    this.addonSelections = {};
    this.initializeAddonState();
    // Defer so addon_controller's change listener is attached when we fire change events.
    queueMicrotask(() => this.applyPrefillFeatures());
  }

  disconnect() {
    this.unbindEvents();
  }

  bindEvents() {
    this.element.addEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.addEventListener("plans:recurrence-changed", this.handleRecurrence);
    this.element.addEventListener("addon:selection-changed", this.handleAddonSelectionChanged);
  }

  unbindEvents() {
    this.element.removeEventListener("plans:plan-changed", this.handlePlanChanged);
    this.element.removeEventListener("plans:recurrence-changed", this.handleRecurrence);
    this.element.removeEventListener("addon:selection-changed", this.handleAddonSelectionChanged);
  }

  // Events

  nextRequest() {
    return ++this._requestId;
  }

  isLatest(id) {
    return id === this._requestId;
  }

  handlePlanChanged = (event) => {
    if (event.detail?.planName) {
      this.onPlanChanged(event.detail.planName);
    } else {
      this.refreshAllPrices();
    }

    this.showPlanSchedule(event.detail?.category);
  };

  handleRecurrence = (event) => {
    if (event.detail?.recurrence) {
      this.onRecurrenceChanged(event.detail.recurrence);
    } else {
      this.recurrenceValueChanged();
    }
  };

  handleAddonSelectionChanged = (event) => {
    const { key, value } = event.detail || {};

    if (key !== undefined) {
      this.updateAddonInState(key, value);
    }

    // No need to refetch prices — addon unit prices don't change based on selection.
    // The addon state is used when buildPlan sends params to the billing preview.
  };

  // Inter-controller communication

  onPlanChanged(planName) {
    this.selectedPlanValue = this.normalizePlanName(planName);
    this.refreshAllPrices();
  }

  onRecurrenceChanged(recurrence) {
    this.recurrenceValue = recurrence;
    this.recurrenceValueChanged();
  }

  normalizePlanName(planName) {
    return this.viewerSelectTarget?.selectedOptions?.[0]?.dataset?.plan || planName;
  }

  handleViewerSelection() {
    const selectedOption = this.viewerSelectTarget?.selectedOptions?.[0];
    if (!selectedOption) return;

    const { plan } = selectedOption.dataset;
    if (!plan) return;

    this.selectedPlanValue = plan;

    const card = this.viewerSelectTarget.closest("[data-category]");
    const category = card?.dataset.category;

    this.dispatch("plan-changed", { detail: { planName: plan, category: category }, bubbles: true });

    if (category) {
      this.refreshPricingForCategory(category);
    } else {
      this.refreshAllPrices();
    }
  }

  initializeViewerCount() {
    if (!this.hasViewerSelectTarget || !this.currentPlanValue) return;
    const option = this.viewerSelectTarget.querySelector(`option[data-plan="${this.currentPlanValue}"]`);
    if (!option) return;

    this.viewerSelectTarget.value = option.value;
    // Setting .value doesn't fire "change", so sync price/selection manually.
    this.handleViewerSelection();
  }

  // Display updates

  updateField(targets, card, baseAmount) {
    const el = targets.find(t => card.contains(t));
    if (!el) return;

    el.dataset.baseAmount = baseAmount;

    const inner = el.querySelector("span.amount[data-base-amount]");
    if (inner) {
      inner.dataset.baseAmount = baseAmount;
    }
  }

  toggleRecurrenceDisplay() {
    const isMonthly = this.recurrenceValue === this.constructor.REC_MONTH;

    this.monthlyPriceTargets.forEach(price => price.classList.toggle("hidden", !isMonthly));
    this.yearlyPriceTargets.forEach(price => price.classList.toggle("hidden", isMonthly));

    this.billingTextTargets.forEach(text => {
      text.classList.toggle("billed-per-month", isMonthly);
      text.classList.toggle("billed-per-year", !isMonthly);
    });

    if (!this.hasPlanScheduleTarget) return;

    this.stepNumberTargets.forEach(number => {
      number.textContent = isMonthly ? number.dataset.withoutSchedule : number.dataset.withSchedule;
    });
  }

  showPlanSchedule(category) {
    if (!this.hasPlanScheduleTarget || !category) return;

    const planName = this.getPlanNameForCategory(category);

    this.planScheduleTargets.forEach(schedule => {
      schedule.classList.toggle("hidden", schedule.dataset.plan !== planName);
    });
  }

  async refreshAllPlanPrices(requestId = null) {
    const fetchPromises = [];

    this.planCardTargets.forEach(card => {
      const category = card.dataset.category;
      if (!category) return;

      const planName = this.getPlanNameForCategory(category);
      if (!planName) return;

      fetchPromises.push(this.fetchBackendPricing(planName, this.recurrenceValue, requestId));
    });

    const results = await Promise.all(fetchPromises);

    results.forEach(data => {
      if (data) this.applyBackendPricing(data);
    });

    this.dispatch("prices-updated");
  }

  async refreshPricingForCategory(category) {
    if (!category) return;

    this.showPlanSchedule(category);

    const planName = this.getPlanNameForCategory(category);
    if (!planName) return;

    const requestId = this.nextRequest();
    const data = await this.fetchBackendPricing(planName, this.recurrenceValue, requestId);

    if (data) {
      this.applyBackendPricing(data);
      this.dispatch("prices-updated");
    }
  }

  async fetchBackendPricing(planName, recurrence, requestId = null) {
    try {
      const response = await fetch("/admin/plan_previews", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({
          plan: {
            plan_type_name: planName,
            recurrence,
            contract_recurrence: recurrence
          }
        })
      });

      if (!response.ok) return;

      const json = await response.json();

      if (requestId && !this.isLatest(requestId)) return;

      return json;
    } catch (e) {
      console.error("Error fetching backend plan preview:", e);
      return;
    }
  }

  applyBackendPricing(data) {
    if (!data?.amounts || data.amounts.per_month == null) return;

    const { per_month, billed_per_period, savings = 0 } = data.amounts;
    const { category, recurrence, available_addons } = data;

    this.updateDisplayedPrices(category, recurrence, per_month, billed_per_period, savings);

    if (available_addons) {
      this.updateAddonPrices(category, available_addons);
    }
  }

  updateAddonPrices(category, addonPrices) {
    this.addonPricesByCategory[category] = addonPrices;

    // Only update the inline addon section with prices for the selected category
    const plansController = this.application.getControllerForElementAndIdentifier(this.element, "plans");
    const selectedCategory = plansController?.selectedCategoryValue;

    if (category === selectedCategory) {
      this.updateInlineAddonPrices(addonPrices);
    }

    this.updateAnnualBillingNotes();
  }

  updateInlineAddonPrices(addonPrices) {
    const section = document.querySelector(this.constructor.INLINE_ADDON_SELECTOR);
    if (!section) return;

    Object.entries(addonPrices).forEach(([addonKey, priceData]) => {
      const addonRow = section.querySelector(`[data-addon="${addonKey}"]`);
      if (!addonRow) return;

      const displayPrice = priceData.display_price || priceData;

      // Update main display price
      addonRow.querySelectorAll(".price [data-base-amount]").forEach(el => {
        el.dataset.baseAmount = displayPrice;
      });

      // Update annual comparison note
      if (priceData.year_price_per_month != null) {
        const annualComparisonEl = addonRow.querySelector("[data-pricing-target='annualComparison'] [data-base-amount]");
        if (annualComparisonEl) {
          annualComparisonEl.dataset.baseAmount = priceData.year_price_per_month;
        }
      }

      // Update annual total
      if (priceData.year_price_total != null) {
        const annualTotalEl = addonRow.querySelector("[data-pricing-target='annualTotal'] [data-base-amount]");
        if (annualTotalEl) {
          annualTotalEl.dataset.baseAmount = priceData.year_price_total;
        }
      }
    });

    // Filter addons by selected plan category
    this.filterInlineAddonsByPlan();
  }

  filterInlineAddonsByPlan() {
    const section = document.querySelector(this.constructor.INLINE_ADDON_SELECTOR);
    if (!section) return;

    const plansController = this.application.getControllerForElementAndIdentifier(this.element, "plans");
    const planCategory = plansController?.selectedCategoryValue;
    if (!planCategory) return;

    // Show/hide addon rows based on which categories they belong to
    section.querySelectorAll("[data-addon-categories]").forEach(addonRow => {
      const categories = addonRow.dataset.addonCategories.split(",");
      const shouldShow = categories.includes(planCategory);
      addonRow.style.display = shouldShow ? "" : "none";
    });

    // Hide empty groups
    section.querySelectorAll(".closeable-group").forEach(group => {
      const visibleAddons = Array.from(
        group.querySelectorAll("[data-addon-categories]")
      ).filter(addon => addon.style.display !== "none");

      group.style.display = visibleAddons.length > 0 ? "" : "none";
    });
  }

  updateAnnualBillingNotes() {
    const isMonthly = this.recurrenceValue === this.constructor.REC_MONTH;

    this.annualComparisonTargets.forEach(note => {
      note.style.display = isMonthly ? "" : "none";
    });

    this.annualTotalTargets.forEach(note => {
      note.style.display = isMonthly ? "none" : "";
    });
  }

  // Helper Methods

  findCardForPlan(category) {
    return this.planCardTargets.find(card => card.dataset.category === category);
  }

  capabilitiesFor(category) {
    const card = this.findCardForPlan(category);
    return JSON.parse(card?.dataset.capabilities || "{}");
  }

  getPlanNameForCategory(category) {
    const card = this.findCardForPlan(category);
    if (!card) return null;

    const capabilities = this.capabilitiesFor(category);

    if (capabilities.hasSelectableViewerLimit && this.hasViewerSelectTarget) {
      return this.viewerSelectTarget.selectedOptions?.[0]?.dataset?.plan ||
             this.viewerSelectTarget.options[0]?.dataset?.plan;
    }

    return card.dataset.plan;
  }

  updateDisplayedPrices(category, recurrence, perMonth, billedPerPeriod, savings) {
    const card = this.findCardForPlan(category);
    if (!card) return;

    if (recurrence === this.constructor.REC_MONTH) {
      this.updateField(this.monthlyAmountTargets, card, perMonth);
    } else {
      this.updateField(this.yearlyAmountTargets, card, perMonth);
      this.updateField(this.savingsAmountTargets, card, savings);
      this.updateField(this.billingAmountTargets, card, billedPerPeriod);
    }
  }

  async recurrenceValueChanged() {
    const requestId = this.nextRequest();
    await this.refreshAllPlanPrices(requestId);

    if (!this.isLatest(requestId)) return;
    this.toggleRecurrenceDisplay();
    this.updateAnnualBillingNotes();
  }

  // Addon State Management

  initializeAddonState() {
    const serverAddons = this.initialAddonsByCategoryValue || {};

    // Flatten all category addons into a single state (inline section is shared)
    Object.values(serverAddons).forEach(addons => {
      Object.entries(addons).forEach(([key, value]) => {
        if (value > 0) {
          this.addonSelections[key] = value;
        }
      });
    });
  }

  // Ticks addons from ?features[X]=qty links, then dispatches so plans_controller can
  // auto-progress (addon-flow → update; plan-flow handles its own progression).
  applyPrefillFeatures() {
    const prefill = this.prefillFeaturesValue || {};
    let anyTicked = false;

    Object.entries(prefill).forEach(([key, qty]) => {
      const quantity = parseInt(qty, 10) || 0;
      if (quantity <= 0) return;

      this.element.querySelectorAll(`input[name="${key}"]`).forEach(input => {
        if (input.type === "checkbox" && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
          anyTicked = true;
        } else if (input.classList.contains("quantity-input")) {
          const current = parseInt(input.value, 10) || 0;
          const max = parseInt(input.max, 10) || Infinity;
          const target = Math.min(current + quantity, max);
          if (target > current) {
            input.value = target;
            input.dispatchEvent(new Event("change", { bubbles: true }));
            anyTicked = true;
          }
        }
      });
    });

    if (anyTicked) {
      this.dispatch("prefill-features-applied");
      this.clearPrefillUrlParams();
    }
  }

  // Removes features[] params from the URL so refresh/back doesn't re-apply (would multi-increment quantity addons).
  clearPrefillUrlParams() {
    if (!window.history.replaceState) return;

    const url = new URL(window.location);
    [...url.searchParams.keys()].filter(k => k.startsWith("features[")).forEach(k => url.searchParams.delete(k));
    window.history.replaceState({}, "", url);
  }

  getAddonsForPlan() {
    return { ...this.addonSelections };
  }

  updateAddonInState(key, value) {
    if (value > 0) {
      this.addonSelections[key] = value;
    } else {
      delete this.addonSelections[key];
    }
  }

  splitRecurringAndOneTimeAddons(addons) {
    const recurring = {};
    const once = {};

    Object.entries(addons || {}).forEach(([key, quantity]) => {
      if ((this.nonRecurringAddonsValue || []).includes(key)) {
        once[key] = quantity;
      } else {
        recurring[key] = quantity;
      }
    });

    return {recurring, once};
  }

  async refreshAllPrices() {
    const requestId = this.nextRequest();

    await this.refreshAllPlanPrices(requestId);
  }
}
